import { Command } from 'commander';
import chalk from 'chalk';
import Table from 'cli-table3';
import { execSync } from 'child_process';
import fs from 'fs-extra';
import { join } from 'path';
import { logger } from '../lib/logger.js';
import { checkInstallation } from '../lib/installer.js';
import { detectProject } from '../lib/detector.js';
import { validateCurrentLicense, getLicenseInfo } from '../lib/license.js';
import { getLatestNpmVersion, isNewerVersion } from '../lib/npm-registry.js';

function isClaudeCodeInstalled(): boolean {
  try {
    execSync('claude --version', { encoding: 'utf-8', timeout: 5000, stdio: 'pipe' });
    return true;
  } catch {
    return false;
  }
}

function getClaudeCodeVersion(): string | null {
  try {
    const out = execSync('claude --version', { encoding: 'utf-8', timeout: 5000, stdio: 'pipe' }).trim();
    return out || null;
  } catch {
    return null;
  }
}
import { loadCache, isHeartbeatDue } from '../lib/license-cache.js';
import { LicenseClient, getLicenseServerUrl } from '../lib/license-client.js';

interface DiagnosticItem {
  name: string;
  status: 'ok' | 'warning' | 'error' | 'info';
  message: string;
  details?: string;
  fix?: string;
  installCmd?: string;
}

type PackageManager = 'winget' | 'brew' | 'apt' | 'dnf' | 'pacman' | null;

function detectPackageManager(): PackageManager {
  const tryCmd = (cmd: string): boolean => {
    try {
      execSync(`${cmd} --version`, { encoding: 'utf-8', timeout: 5000, stdio: 'pipe' });
      return true;
    } catch { return false; }
  };

  if (process.platform === 'win32' && tryCmd('winget')) return 'winget';
  if (process.platform === 'darwin' && tryCmd('brew')) return 'brew';
  if (process.platform === 'linux') {
    if (tryCmd('apt')) return 'apt';
    if (tryCmd('dnf')) return 'dnf';
    if (tryCmd('pacman')) return 'pacman';
  }
  return null;
}

interface InstallInfo {
  winget?: string;
  brew?: string;
  apt?: string;
  dnf?: string;
  pacman?: string;
}

function getInstallCmd(pm: PackageManager, info: InstallInfo): string | undefined {
  if (!pm) return undefined;
  return info[pm];
}

function resolvePodmanCmd(): string {
  // Try PATH first
  try {
    execSync('podman --version', { encoding: 'utf-8', timeout: 5000, stdio: 'pipe' });
    return 'podman';
  } catch { /* not in PATH */ }

  // On Windows, check known install paths
  if (process.platform === 'win32') {
    const knownPaths = [
      'C:\\Program Files\\RedHat\\Podman\\podman.exe',
      join(process.env.LOCALAPPDATA || '', 'RedHat', 'Podman', 'podman.exe'),
    ];
    for (const p of knownPaths) {
      if (require('fs').existsSync(p)) return p;
    }
  }
  return 'podman';
}

function getContainerEngineStatus(): { name: string; version: string | null; running: boolean } | null {
  // Try Docker first
  try {
    const dockerVer = execSync('docker --version', { encoding: 'utf-8', timeout: 5000, stdio: 'pipe' }).trim();
    const dockerInfo = (() => { try { execSync('docker info', { encoding: 'utf-8', timeout: 10000, stdio: 'pipe' }); return true; } catch { return false; } })();
    return { name: 'Docker', version: dockerVer.replace('Docker version ', '').split(',')[0], running: dockerInfo };
  } catch { /* not installed */ }

  // Try Podman (with path resolution for Windows)
  try {
    const podmanCmd = resolvePodmanCmd();
    const podmanVer = execSync(`${podmanCmd} --version`, { encoding: 'utf-8', timeout: 5000, stdio: 'pipe' }).trim();
    // Check if Podman machine is actually running
    const podmanRunning = (() => { try { execSync(`${podmanCmd} info`, { encoding: 'utf-8', timeout: 10000, stdio: 'pipe' }); return true; } catch { return false; } })();
    return { name: 'Podman', version: podmanVer.replace('podman version ', ''), running: podmanRunning };
  } catch { /* not installed */ }

  return null;
}

/**
 * Check Windows virtualization prerequisites: WSL installed + Hyper-V/VMP enabled.
 */
function checkWindowsVirt(): 'wsl-missing' | 'hyperv-disabled' | null {
  // 1. WSL installed?
  try {
    execSync('wsl --status', { encoding: 'utf-8', timeout: 5000, stdio: 'pipe' });
  } catch { return 'wsl-missing'; }

  // 2. PowerShell check: VirtualMachinePlatform feature
  try {
    const state = execSync(
      'powershell -NoProfile -Command "(Get-WindowsOptionalFeature -Online -FeatureName VirtualMachinePlatform).State"',
      { encoding: 'utf-8', timeout: 10000, stdio: 'pipe' },
    ).trim();
    if (state === 'Disabled') return 'hyperv-disabled';
  } catch { /* PowerShell not available or not admin — skip */ }

  return null;
}

/**
 * Get Node.js version
 */
function getNodeVersion(): string {
  return process.version;
}

/**
 * Check if .NET SDK is installed
 */
function getDotNetVersion(): string | null {
  try {
    const output = execSync('dotnet --version', {
      encoding: 'utf-8',
      timeout: 5000,
      stdio: ['pipe', 'pipe', 'pipe'],
    });
    return output.trim();
  } catch {
    return null;
  }
}

/**
 * Check if Git is installed
 */
function getGitVersion(): string | null {
  try {
    const output = execSync('git --version', {
      encoding: 'utf-8',
      timeout: 5000,
      stdio: ['pipe', 'pipe', 'pipe'],
    });
    return output.replace('git version ', '').trim();
  } catch {
    return null;
  }
}

/**
 * Get npm version
 */
function getNpmVersion(): string | null {
  try {
    const output = execSync('npm --version', {
      encoding: 'utf-8',
      timeout: 5000,
      stdio: ['pipe', 'pipe', 'pipe'],
    });
    return output.trim();
  } catch {
    return null;
  }
}

export const doctorCommand = new Command('doctor')
  .description('Run diagnostics and check SmartStack health')
  .option('--json', 'Output as JSON')
  .option('-v, --verbose', 'Show detailed information')
  .option('--fix', 'Auto-install missing tools using system package manager')
  .action(async (options) => {
    const diagnostics: DiagnosticItem[] = [];
    const pkg = JSON.parse(await fs.readFile(join(__dirname, '..', 'package.json'), 'utf-8'));
    const pm = options.fix ? detectPackageManager() : null;

    // Header
    if (!options.json) {
      console.log(chalk.cyan(`
╔══════════════════════════════════════════════════════════╗
║                                                          ║
║   ${chalk.bold('SmartStack Doctor')} - System Diagnostics              ║
║   Version ${pkg.version}                                          ║
║                                                          ║
╚══════════════════════════════════════════════════════════╝
`));
    }

    // 1. License Check
    const licenseResult = await validateCurrentLicense();
    const licenseInfo = await getLicenseInfo();

    if (licenseResult.valid && licenseInfo) {
      diagnostics.push({
        name: 'License',
        status: licenseInfo.daysRemaining < 30 ? 'warning' : 'ok',
        message: `Valid (${licenseInfo.edition})`,
        details: `Expires in ${licenseInfo.daysRemaining} days`,
      });
    } else {
      diagnostics.push({
        name: 'License',
        status: 'error',
        message: licenseResult.error || 'Not activated',
        fix: 'smartstack license activate <key>',
      });
    }

    // 1b. License Server Health
    try {
      const baseUrl = await getLicenseServerUrl();
      const client = new LicenseClient(baseUrl);
      const healthy = await client.healthCheck();
      diagnostics.push({
        name: 'License Server',
        status: healthy ? 'ok' : 'warning',
        message: healthy ? `Reachable (${baseUrl})` : 'Unreachable',
        details: !healthy ? 'License server is not responding. Online validation will use cache.' : undefined,
      });
    } catch {
      diagnostics.push({
        name: 'License Server',
        status: 'warning',
        message: 'Could not check server health',
      });
    }

    // 1c. License Activation & Heartbeat
    const cache = await loadCache();
    if (cache.activation) {
      diagnostics.push({
        name: 'Activation',
        status: 'ok',
        message: `Active since ${cache.activation.activatedAt?.split('T')[0] || 'unknown'}`,
        details: `Fingerprint: ${cache.activation.machineFingerprint?.substring(0, 12)}...`,
      });

      const heartbeatDue = isHeartbeatDue(cache);
      diagnostics.push({
        name: 'Heartbeat',
        status: heartbeatDue ? 'warning' : 'ok',
        message: heartbeatDue
          ? `Overdue (last: ${cache.lastHeartbeat || 'never'})`
          : `OK (last: ${cache.lastHeartbeat?.split('T')[0] || 'N/A'})`,
        details: `Interval: ${cache.activation.heartbeatIntervalHours}h`,
        fix: heartbeatDue ? 'Run any command to trigger a heartbeat' : undefined,
      });
    } else {
      diagnostics.push({
        name: 'Activation',
        status: licenseResult.valid ? 'warning' : 'info',
        message: 'Not activated on this machine',
        fix: 'smartstack license activate <key>',
      });
    }

    // 2. Node.js
    const nodeVersion = getNodeVersion();
    const nodeVersionNum = parseInt(nodeVersion.replace('v', '').split('.')[0], 10);
    diagnostics.push({
      name: 'Node.js',
      status: nodeVersionNum >= 18 ? 'ok' : 'error',
      message: nodeVersion,
      details: nodeVersionNum < 18 ? 'Requires Node.js 18+' : undefined,
      fix: nodeVersionNum < 18 ? 'Install Node.js 18 or later' : undefined,
    });

    // 3. npm
    const npmVersion = getNpmVersion();
    diagnostics.push({
      name: 'npm',
      status: npmVersion ? 'ok' : 'warning',
      message: npmVersion || 'Not found',
    });

    // 4. Git
    const gitVersion = getGitVersion();
    diagnostics.push({
      name: 'Git',
      status: gitVersion ? 'ok' : 'error',
      message: gitVersion || 'Not installed',
      fix: !gitVersion ? 'Install Git: https://git-scm.com/' : undefined,
      installCmd: !gitVersion ? getInstallCmd(pm, {
        winget: 'winget install -e --id Git.Git',
        brew: 'brew install git',
        apt: 'sudo apt install -y git',
        dnf: 'sudo dnf install -y git',
        pacman: 'sudo pacman -S --noconfirm git',
      }) : undefined,
    });

    // 5. .NET SDK
    const dotnetVersion = getDotNetVersion();
    diagnostics.push({
      name: '.NET SDK',
      status: dotnetVersion ? 'ok' : 'warning',
      message: dotnetVersion || 'Not installed',
      details: !dotnetVersion ? 'Required for .NET projects' : undefined,
      fix: !dotnetVersion ? 'Install .NET SDK: https://dotnet.microsoft.com/' : undefined,
      installCmd: !dotnetVersion ? getInstallCmd(pm, {
        winget: 'winget install -e --id Microsoft.DotNet.SDK.9',
        brew: 'brew install dotnet',
        apt: 'sudo apt install -y dotnet-sdk-9.0',
        dnf: 'sudo dnf install -y dotnet-sdk-9.0',
      }) : undefined,
    });

    // 5b. Docker / Podman
    const containerEngine = getContainerEngineStatus();
    if (containerEngine && containerEngine.running) {
      diagnostics.push({
        name: 'Container Engine',
        status: 'ok',
        message: `${containerEngine.name} ${containerEngine.version}`,
      });
    } else if (containerEngine && !containerEngine.running) {
      const isPodman = containerEngine.name === 'Podman';
      // Check WSL + Hyper-V availability on Windows (required by Podman)
      const virtIssue = isPodman && process.platform === 'win32' ? checkWindowsVirt() : null;
      const virtMessage = virtIssue === 'wsl-missing'
        ? 'Podman installed but WSL2 is missing'
        : virtIssue === 'hyperv-disabled'
          ? 'Podman installed but hardware virtualization is not enabled'
          : `${containerEngine.name} installed but ${isPodman ? 'machine' : 'daemon'} not running`;
      const virtFix = virtIssue === 'wsl-missing'
        ? 'Run as Admin: wsl --install, restart PC, then: podman machine init && podman machine start'
        : virtIssue === 'hyperv-disabled'
          ? 'Enable VT-x/SVM in BIOS, then run as Admin: wsl --install --no-distribution, restart, then: podman machine init && podman machine start'
          : isPodman
            ? 'Run: podman machine init && podman machine start'
            : 'Start Docker Desktop, or run: podman machine init && podman machine start';
      diagnostics.push({
        name: 'Container Engine',
        status: 'warning',
        message: virtMessage,
        fix: virtFix,
        installCmd: virtIssue === 'wsl-missing' ? (process.platform === 'win32' ? 'wsl --install' : undefined) : undefined,
      });
    } else {
      diagnostics.push({
        name: 'Container Engine',
        status: 'warning',
        message: 'Not installed (Docker or Podman)',
        details: 'Required for ss docker commands',
        fix: 'Install Docker Desktop or Podman',
        installCmd: getInstallCmd(pm, {
          winget: 'winget install -e --id RedHat.Podman',
          brew: 'brew install podman',
          apt: 'sudo apt install -y podman',
          dnf: 'sudo dnf install -y podman',
          pacman: 'sudo pacman -S --noconfirm podman',
        }),
      });
    }

    // 6. Claude Code
    const claudeInstalled = isClaudeCodeInstalled();
    const claudeVersion = getClaudeCodeVersion();
    diagnostics.push({
      name: 'Claude Code',
      status: claudeInstalled ? 'ok' : 'error',
      message: claudeVersion || 'Not installed',
      fix: !claudeInstalled
        ? process.platform === 'win32'
          ? 'Windows: irm https://claude.ai/install.ps1 | iex | WSL: curl -fsSL https://claude.ai/install.sh | bash'
          : 'curl -fsSL https://claude.ai/install.sh | bash'
        : undefined,
    });

    // 6bis. CLI Update — is a newer @atlashub/smartstack-cli published?
    // (support triage: an outdated CLI is the FIRST thing to rule out before
    // any defect report — /support-report applies the same gate client-side)
    const latestCli = await getLatestNpmVersion('@atlashub/smartstack-cli', false, { quiet: true });
    if (latestCli === null) {
      diagnostics.push({
        name: 'CLI Update',
        status: 'info',
        message: `v${pkg.version} installed — npm registry unreachable, cannot check for updates`,
      });
    } else if (isNewerVersion(latestCli, pkg.version)) {
      diagnostics.push({
        name: 'CLI Update',
        status: 'warning',
        message: `v${latestCli} available (installed: v${pkg.version})`,
        fix: 'npm i -g @atlashub/smartstack-cli@latest && ss update',
      });
    } else {
      diagnostics.push({
        name: 'CLI Update',
        status: 'ok',
        message: `v${pkg.version} is the latest`,
      });
    }

    // 7. SmartStack Installation
    const installation = await checkInstallation({ global: true });
    diagnostics.push({
      name: 'SmartStack Commands',
      status: installation.installed ? 'ok' : 'warning',
      message: installation.installed
        ? `${installation.components.commands.count} commands`
        : 'Not installed',
      fix: !installation.installed ? 'smartstack install' : undefined,
    });

    diagnostics.push({
      name: 'SmartStack Agents',
      status: installation.components.agents.installed ? 'ok' : 'warning',
      message: installation.components.agents.installed
        ? `${installation.components.agents.count} agents`
        : 'Not installed',
    });

    diagnostics.push({
      name: 'SmartStack Skills',
      status: installation.components.skills.installed ? 'ok' : 'warning',
      message: installation.components.skills.installed
        ? `${installation.components.skills.count} skills`
        : 'Not installed',
    });

    diagnostics.push({
      name: 'GitFlow Config',
      status: installation.gitflow.configExists ? 'ok' : 'info',
      message: installation.gitflow.configExists ? 'Configured' : 'Not configured',
    });

    // 9. BA Develop — Skill (global)
    const baDevelopSkillPath = join(installation.location, 'skills', 'ba-develop', 'SKILL.md');
    const baDevelopSkillExists = await fs.pathExists(baDevelopSkillPath);
    diagnostics.push({
      name: 'BA Develop Skill',
      status: baDevelopSkillExists ? 'ok' : 'warning',
      message: baDevelopSkillExists ? 'Installed' : 'Not installed',
      details: 'Global skill (~/.claude/skills/ba-develop/)',
      fix: !baDevelopSkillExists ? 'smartstack install' : undefined,
    });

    // 10. Current Project
    const project = await detectProject();
    if (project.isGitRepo) {
      diagnostics.push({
        name: 'Git Repository',
        status: 'ok',
        message: `Branch: ${project.currentBranch || 'unknown'}`,
      });
    }

    if (project.hasDotNet) {
      diagnostics.push({
        name: '.NET Project',
        status: 'ok',
        message: `${project.csprojFiles.length} project(s)`,
        details: project.hasEfCore ? 'EF Core detected' : undefined,
      });
    }

    // Output results
    if (options.json) {
      const summary = {
        version: pkg.version,
        timestamp: new Date().toISOString(),
        diagnostics,
        summary: {
          total: diagnostics.length,
          ok: diagnostics.filter(d => d.status === 'ok').length,
          warnings: diagnostics.filter(d => d.status === 'warning').length,
          errors: diagnostics.filter(d => d.status === 'error').length,
        },
      };
      console.log(JSON.stringify(summary, null, 2));
      process.exit(summary.summary.errors > 0 ? 1 : 0);
    }

    // Human-readable output
    const table = new Table({
      head: [
        chalk.cyan('Component'),
        chalk.cyan('Status'),
        chalk.cyan('Details'),
      ],
      style: { head: [], border: [] },
      colWidths: [25, 20, 40],
    });

    for (const item of diagnostics) {
      const statusIcon = {
        ok: chalk.green('✓ OK'),
        warning: chalk.yellow('⚠ Warning'),
        error: chalk.red('✗ Error'),
        info: chalk.blue('ℹ Info'),
      }[item.status];

      let details = item.message;
      if (options.verbose && item.details) {
        details += `\n${chalk.gray(item.details)}`;
      }

      table.push([item.name, statusIcon, details]);
    }

    console.log(table.toString());
    console.log();

    // Summary
    const okCount = diagnostics.filter(d => d.status === 'ok').length;
    const warnCount = diagnostics.filter(d => d.status === 'warning').length;
    const errorCount = diagnostics.filter(d => d.status === 'error').length;

    console.log(chalk.bold('Summary:'));
    console.log(`  ${chalk.green('✓')} ${okCount} OK`);
    if (warnCount > 0) console.log(`  ${chalk.yellow('⚠')} ${warnCount} Warnings`);
    if (errorCount > 0) console.log(`  ${chalk.red('✗')} ${errorCount} Errors`);
    console.log();

    // Show fixes if there are errors
    const itemsWithFixes = diagnostics.filter(d => d.fix && (d.status === 'error' || d.status === 'warning'));
    if (itemsWithFixes.length > 0 && !options.fix) {
      console.log(chalk.bold('Recommended fixes:'));
      for (const item of itemsWithFixes) {
        console.log(`  ${item.name}: ${chalk.cyan(item.fix)}`);
      }
      console.log();
      const fixableCount = diagnostics.filter(d => d.installCmd).length;
      if (fixableCount > 0) {
        console.log(chalk.gray(`  Tip: Run ${chalk.cyan('ss doctor --fix')} to auto-install ${fixableCount} missing tool(s)`));
        console.log();
      }
    }

    // --fix mode: auto-install missing tools
    if (options.fix) {
      const fixable = diagnostics.filter(d => d.installCmd && (d.status === 'error' || d.status === 'warning'));
      if (fixable.length === 0) {
        console.log(chalk.green('Nothing to fix — all installable tools are present.'));
        console.log();
      } else if (!pm) {
        logger.error('No supported package manager found (winget, brew, apt, dnf, pacman).');
        console.log(`  Please install the missing tools manually.`);
        console.log();
      } else {
        console.log(chalk.bold(`Auto-fix using ${chalk.cyan(pm)}:`));
        console.log();
        let fixed = 0;
        for (const item of fixable) {
          console.log(`  ${chalk.yellow('Installing')} ${item.name}: ${chalk.gray(item.installCmd!)}`);
          try {
            execSync(item.installCmd!, { encoding: 'utf-8', stdio: 'inherit', timeout: 300000 });
            console.log(`  ${chalk.green('✓')} ${item.name} installed`);
            fixed++;
          } catch (err: unknown) {
            // winget returns non-zero when package is already installed — detect and treat as success
            const stderr = (err as { stderr?: string }).stderr || '';
            const stdout = (err as { stdout?: string }).stdout || '';
            const output = stderr + stdout;
            const alreadyInstalled = /already installed|déjà.*install|no applicable update|introuvable/i.test(output);
            if (alreadyInstalled) {
              console.log(`  ${chalk.green('✓')} ${item.name} already installed (restart terminal if not detected)`);
              fixed++;
            } else {
              console.log(`  ${chalk.red('✗')} ${item.name} install failed — try manually: ${chalk.cyan(item.installCmd!)}`);
            }
          }
          console.log();
        }
        console.log(chalk.bold(`Fixed: ${fixed}/${fixable.length}`));
        if (fixed > 0) {
          console.log(chalk.gray('  Restart your terminal, then run ss doctor to verify.'));
        }
        console.log();
      }
    }

    // Final verdict
    if (errorCount === 0) {
      logger.box([
        'SmartStack is healthy!',
        '',
        warnCount > 0
          ? `${warnCount} warning(s) can be addressed for optimal experience.`
          : 'All systems operational.',
      ], 'success');
      process.exit(0);
    } else {
      logger.box([
        `${errorCount} critical issue(s) found!`,
        '',
        'Please fix the errors above before using SmartStack.',
      ], 'error');
      process.exit(1);
    }
  });
